/* eslint-disable no-param-reassign */
import React, {
  useContext,
  useEffect,
  useCallback,
  useState,
  useRef,
  Context,
  MutableRefObject,
} from 'react';
import { toast } from 'react-toastify';
import { observer } from 'mobx-react-lite';
import { CircularProgress } from '@material-ui/core';
import { Paper, Typography, TypographyBody, Button } from './styles';
import { FormStoreContext, FormStore, resetFormStoreContext } from '../../store/FormStore';
import { instance } from '../../services/api';
import { GlobalsContext } from '../../store/Globals';
import CurlPanel from '../../components/CurlPanel';
import { fromJsonToFormContext, getForm } from './utils';
import { InputTypes } from '../../store/inputs/types';  

const UpdateTypeFormGenerator: React.FC<{ asset: { name: { label: string; tag: string }; inputs: InputTypes[] } }> = observer(
  ({ asset }) => {
    const [loading, setLoading] = useState(false);
    const globals = useContext(GlobalsContext);
    const { dataTypeList } = globals;
    const [initialInputs, setInitialInputs] = useState<InputTypes[]>([]);
    const context = useCallback(() => {
      return FormStoreContext([
        ...Object.keys(asset.inputs).map((prop, idx) => {
          return fromJsonToFormContext(
            (asset.inputs as any)[prop],
            asset.inputs[idx] ? asset.inputs[idx].tag : prop,
            dataTypeList,
          );
        }),
      ]);
    }, [asset, dataTypeList]);
    const Forms: MutableRefObject<FormStore> = useRef(
      useContext(context() as Context<FormStore>),
    );
    const [jsonObj, setJsonObj] = useState<any>({});

    const getItem = useCallback(async (assetTag: string) => {
      try {
        const { data } = await instance.post('/api/query/getSchema/', {
          assetType: assetTag,
        });

        return data;
      } catch (err: any) {
        toast.error(err.message);

        return null;
      }
    }, []);

    // Copy an array with no references
    const copyArray = useCallback((aObject: any) => {
      if (!aObject) return aObject;

      const bObject = Array.isArray(aObject) ? [] : {};

      Object.entries(aObject).forEach((entrie) => {
        const key = entrie[0];
        const value = entrie[1];

        (bObject as any)[key] =
          typeof value === 'object' ? copyArray(value) : value;
      });

      return bObject;
    }, []);

    const setInitInputs = useCallback(async () => {
      try {
        const result = await getItem(asset.name.tag);

        Forms.current.inputs.forEach((input) => {
          if (input.tag === 'tag') {
            input.value = result.tag;
          } else if (input.tag === 'description') {
            input.value = result.description;
          } else if (input.tag === 'label') {
            input.value = result.label;
          } else if (input.tag === 'props') {
            const newRecords: Record<string, any>[] = [];
            if (result.props.length > 0) {
              result.props.forEach((prop: any) => {
                newRecords.push({
                  tag: prop.tag,
                  dataType: prop.dataType,
                  writers: prop.writers,
                  label: prop.label,
                  description: prop.description,
                  required: prop.required,
                  defaultValue: prop.defaultValue,
                  readOnly: prop.readOnly,
                  isKey: prop.isKey,
                });
              });
            }
            input.value = newRecords;
            input.selectorValue = null;
          }
        });

        setInitialInputs(copyArray(Forms.current.inputs));
      } catch (err: any) {
        toast.error(err.message);
      }
    }, [Forms, asset.name.tag, getItem, copyArray]);

    // Reset form store context on unmount to prevent stale data
    useEffect(() => {
      return () => {
        resetFormStoreContext();
      };
    }, []);

    useEffect(() => {
      (async () => {
        setLoading(true);
        await setInitInputs();
        setLoading(false);
      })();
    }, [Forms, asset.name.tag, copyArray, setInitInputs, getItem]);

    const getPropsSubmission = (props: any) => {
      initialInputs.forEach((input) => {
        if (input.tag === 'props') {
          (input.value as any[]).forEach((prop: any) => {
            let found = false;
            props.forEach((p: any) => {
              if (p.tag === prop.tag) {
                found = true;
              }
            });
            if (!found) {
              props.push({
                tag: prop.tag,
                delete: true,
              });
            }
          });
        }
      });

      return props;
    };

    const getTrasactionRequest = () => {
      const obj = Forms.current.getData();
      return {
        assetTypes: [
          {
            tag: obj.tag,
            label: obj.label,
            description: obj.description,
            props: getPropsSubmission(obj.props),
          },
        ],
      };
    };

    // Submit the transaction
    const submit = async (execute: boolean) => {
      setLoading(true);

      const obj = getTrasactionRequest();

      if (execute) {
        Forms.current.toggleInputsDisabled();
      }

      try {
        if (execute) {
          await instance.post('/api/invoke/updateAssetType', obj);
        } else {
          setJsonObj(obj);
        }

        if (execute) {
          const message = `${asset.name.label} transaction commited successfully`;
          toast.success(message);
        }
      } catch (err: any) {
        const message = `Error while trying to commit transaction ${
          asset.name.label
        }: ${err.response ? err.response.data?.error : err}`;

        toast.error(message);
      } finally {
        setLoading(false);
      }

      if (execute) {
        Forms.current.toggleInputsDisabled();
      }
    };

    return (
      <div style={{ marginBottom: '30px' }}>
        <CurlPanel
          method="POST"
          url="/api/invoke/updateAssetType'"
          obj={jsonObj}
          update={submit}
        />
        <Paper style={{ marginTop: '10px' }}>
          <Typography>{asset.name.label}</Typography>
          {Forms.current.inputs.map((form) => getForm(form, dataTypeList))}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </div>
          ) : (
            <Button
              onClick={() => submit(true)}
              disabled={!globals.dynamicTxsAllowed}
            >
              Submit
            </Button>
          )}
        </Paper>
        {!globals.dynamicTxsAllowed ? (
          <TypographyBody color="error">
            This transaction is not permitted to be called by this organization
          </TypographyBody>
        ) : null}
      </div>
    );
  },
);

export default UpdateTypeFormGenerator;
